'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarket } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
import type { ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { onEvent } from '@/events';
interface UseThesisReplyAccessArgs {
  thesisSignature: string;
  marketPubkey: string;
  isAuthenticated: boolean;
  isMobile: boolean;
  writeAccess: ThesisReplyWriteAccess;
  openTradeForm?: () => void;
}
interface ThesisReplyAccess {
  accessRefreshPending: boolean;
  canComposeReply: boolean;
  canTrade: boolean;
  eligibility: ThesisReplyWriteAccess['eligibility'];
  isChecking: boolean;
  isError: boolean;
  isGateVisible: boolean;
  rejectAccess: () => void;
  retry: () => void;
  tradeNow: () => void;
}
export function useThesisReplyAccess(t0) {
  const $ = _c(32);
  const {
    thesisSignature,
    marketPubkey,
    isAuthenticated,
    isMobile,
    writeAccess,
    openTradeForm
  } = t0;
  const [rejectedAtEligibilityUpdate, setRejectedAtEligibilityUpdate] = useState(null);
  const [tradeRequestedAt, setTradeRequestedAt] = useState(null);
  const gateShownRef = useRef(null);
  const eligibilityExperienceVisible = isAuthenticated && writeAccess.status !== "disabled" && writeAccess.status !== "signed-out" && writeAccess.status !== "eligible";
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = eligibilityExperienceVisible && !isMobile;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const marketQuery = useGetMarket(t1, t3);
  const eligibility = writeAccess.eligibility;
  const rejectionPending = rejectedAtEligibilityUpdate !== null && writeAccess.dataUpdatedAt <= rejectedAtEligibilityUpdate;
  const canComposeReply = writeAccess.status === "eligible" && !rejectionPending;
  const canTrade = eligibility?.canTrade === true || marketQuery.data?.status === "trading";
  const isGateVisible = writeAccess.status === "ineligible" || rejectionPending;
  const isError = eligibilityExperienceVisible && writeAccess.status === "error";
  const isChecking = eligibilityExperienceVisible && writeAccess.status === "checking";
  const accessRefreshPending = tradeRequestedAt !== null && writeAccess.dataUpdatedAt <= tradeRequestedAt;
  let t4;
  let t5;
  if ($[4] !== canTrade || $[5] !== isGateVisible || $[6] !== marketPubkey) {
    t4 = () => {
      if (!isGateVisible) {
        return;
      }
      const shownKey = `${marketPubkey}-${String(canTrade)}`;
      if (gateShownRef.current === shownKey) {
        return;
      }
      gateShownRef.current = shownKey;
      onEvent("social_thesis_reply_gate_shown", {
        marketPubkey,
        canTrade
      });
    };
    t5 = [canTrade, isGateVisible, marketPubkey];
    $[4] = canTrade;
    $[5] = isGateVisible;
    $[6] = marketPubkey;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== tradeRequestedAt) {
    t6 = () => {
      if (tradeRequestedAt === null) {
        return;
      }
      const timeout = window.setTimeout(() => setTradeRequestedAt(null), 10000);
      return () => window.clearTimeout(timeout);
    };
    t7 = [tradeRequestedAt];
    $[9] = tradeRequestedAt;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== writeAccess) {
    t8 = function rejectAccess() {
      setRejectedAtEligibilityUpdate(writeAccess.dataUpdatedAt);
      writeAccess.refresh();
    };
    $[12] = writeAccess;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const rejectAccess = t8;
  let t9;
  if ($[14] !== writeAccess) {
    t9 = function retry() {
      writeAccess.refresh();
    };
    $[14] = writeAccess;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const retry = t9;
  let t10;
  if ($[16] !== marketPubkey || $[17] !== openTradeForm || $[18] !== thesisSignature || $[19] !== writeAccess.dataUpdatedAt) {
    t10 = function tradeNow() {
      const tradeForm = openTradeForm === undefined ? document.getElementById("market-trade-form") : null;
      if (openTradeForm === undefined && tradeForm === null) {
        return;
      }
      onEvent("social_thesis_trade_clicked", {
        signature: thesisSignature,
        marketPubkey,
        source: "reply_gate"
      });
      setTradeRequestedAt(writeAccess.dataUpdatedAt);
      if (openTradeForm !== undefined) {
        openTradeForm();
        return;
      }
      if (tradeForm === null) {
        return;
      }
      tradeForm.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      tradeForm.focus({
        preventScroll: true
      });
    };
    $[16] = marketPubkey;
    $[17] = openTradeForm;
    $[18] = thesisSignature;
    $[19] = writeAccess.dataUpdatedAt;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const tradeNow = t10;
  let t11;
  if ($[21] !== accessRefreshPending || $[22] !== canComposeReply || $[23] !== canTrade || $[24] !== eligibility || $[25] !== isChecking || $[26] !== isError || $[27] !== isGateVisible || $[28] !== rejectAccess || $[29] !== retry || $[30] !== tradeNow) {
    t11 = {
      accessRefreshPending,
      canComposeReply,
      canTrade,
      eligibility,
      isChecking,
      isError,
      isGateVisible,
      rejectAccess,
      retry,
      tradeNow
    };
    $[21] = accessRefreshPending;
    $[22] = canComposeReply;
    $[23] = canTrade;
    $[24] = eligibility;
    $[25] = isChecking;
    $[26] = isError;
    $[27] = isGateVisible;
    $[28] = rejectAccess;
    $[29] = retry;
    $[30] = tradeNow;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}